/**
 * Spark Liveness Protocol Types
 *
 * Types for the Spark Liveness authentication protocol.
 * Used for console login, OAuth flows, and P2P verification.
 */
// ============================================================================
// Core Types
// ============================================================================
/** Encryption method for payloads */
export var EncryptionType;
(function (EncryptionType) {
    EncryptionType[EncryptionType["None"] = 0] = "None";
    EncryptionType[EncryptionType["Symmetric"] = 1] = "Symmetric";
    EncryptionType[EncryptionType["MlKem"] = 2] = "MlKem";
})(EncryptionType || (EncryptionType = {}));
// ============================================================================
// Lattice Topics
// ============================================================================
export const SPARK_TOPICS = {
    /** Payload envelope topic - TTL: 5 minutes */
    PAYLOAD: (challengeId) => `/spark/payload/${challengeId}`,
    /** Liveness event topic - TTL: 2 minutes */
    LIVENESS: (walletId) => `/spark/live/${walletId}`,
    /** Auth response topic - TTL: 2 minutes */
    AUTH: (sessionId) => `/spark/auth/${sessionId}`,
};
export const SPARK_TTL = {
    /** Payload envelope TTL in seconds */
    PAYLOAD: 300,
    /** Liveness event TTL in seconds */
    LIVENESS: 120,
    /** Auth response TTL in seconds */
    AUTH: 120,
};
// ============================================================================
// Console-specific action types
// ============================================================================
/** Console Spark Actions - namespace io.estream/console.* */
export const CONSOLE_ACTIONS = {
    LOGIN: 'io.estream/console.login',
    ADMIN_ACTION: 'io.estream/console.admin_action',
    VIEW_GOVERNANCE: 'io.estream/console.view_governance',
};
/** Genesis Spark Actions - namespace io.estream/genesis.* */
export const GENESIS_ACTIONS = {
    /** Add a key holder to genesis configuration */
    ADD_KEY_HOLDER: 'io.estream/genesis.add_key_holder',
    /** Sign the genesis proposal to approve network creation */
    SIGN_PROPOSAL: 'io.estream/genesis.sign_proposal',
    /** Emergency revoke of genesis process */
    REVOKE_GENESIS: 'io.estream/genesis.revoke_genesis',
};
// ============================================================================
// Utilities
// ============================================================================
/** Generate a random challenge ID (32 bytes, hex) */
export function generateChallengeId() {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return bytesToHex(bytes);
}
/** Generate a random nonce (16 bytes, hex) */
export function generateNonce() {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return bytesToHex(bytes);
}
/** Generate a session ID */
export function generateSessionId() {
    return `session-${Date.now()}-${generateNonce().slice(0, 16)}`;
}
function bytesToHex(bytes) {
    return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}
/** Create an unencrypted auth payload for console login */
export function createConsoleLoginPayload(options) {
    const sessionId = generateSessionId();
    const challengeNonce = generateChallengeId();
    const challengeId = generateChallengeId();
    const expiresAt = Date.now() + (options.expiresIn ?? 300_000);
    const payload = {
        action: CONSOLE_ACTIONS.LOGIN,
        service: 'console.estream.io',
        service_wallet_id: options.serviceWalletId ?? '0'.repeat(64),
        session_id: sessionId,
        challenge_nonce: challengeNonce,
        response_lattice: options.serviceLattice,
        expires_at: expiresAt,
        service_signature: '', // Not signed in browser - service verifies via lattice
    };
    const payloadJson = JSON.stringify(payload);
    const payloadBase64 = btoa(payloadJson);
    const envelope = {
        challenge_id: challengeId,
        payload: payloadBase64,
        encryption: EncryptionType.None,
    };
    return { payload, envelope };
}
/** Parse a payload envelope received from lattice */
export function parsePayloadEnvelope(envelope) {
    try {
        if (envelope.encryption !== EncryptionType.None) {
            console.warn('[SparkLiveness] Encrypted payloads not yet supported in browser');
            return null;
        }
        const payloadJson = atob(envelope.payload);
        return JSON.parse(payloadJson);
    }
    catch (e) {
        console.error('[SparkLiveness] Failed to parse payload:', e);
        return null;
    }
}
// ============================================================================
// Genesis Utilities
// ============================================================================
/** Create a Spark challenge for key holder registration */
export function createGenesisKeyHolderChallenge(options) {
    const sessionId = generateSessionId();
    const challengeNonce = generateChallengeId();
    const challengeId = generateChallengeId();
    const expiresAt = Date.now() + (options.expiresIn ?? 300_000);
    const payload = {
        action: GENESIS_ACTIONS.ADD_KEY_HOLDER,
        network_name: options.networkName,
        key_holder_index: options.keyHolderIndex,
        key_holder_name: options.keyHolderName,
        session_id: sessionId,
        challenge_nonce: challengeNonce,
        expires_at: expiresAt,
    };
    const payloadJson = JSON.stringify(payload);
    const payloadBase64 = btoa(payloadJson);
    const envelope = {
        challenge_id: challengeId,
        payload: payloadBase64,
        encryption: EncryptionType.None,
    };
    return { payload, envelope };
}
/** Create a Spark challenge for genesis proposal signing */
export function createGenesisProposalChallenge(options) {
    const sessionId = generateSessionId();
    const challengeNonce = generateChallengeId();
    const challengeId = generateChallengeId();
    const expiresAt = Date.now() + (options.expiresIn ?? 300_000);
    const payload = {
        action: GENESIS_ACTIONS.SIGN_PROPOSAL,
        network_name: options.networkName,
        proposal_id: options.proposalId,
        config_hash: options.configHash,
        session_id: sessionId,
        challenge_nonce: challengeNonce,
        expires_at: expiresAt,
    };
    const payloadJson = JSON.stringify(payload);
    const payloadBase64 = btoa(payloadJson);
    const envelope = {
        challenge_id: challengeId,
        payload: payloadBase64,
        encryption: EncryptionType.None,
    };
    return { payload, envelope };
}
//# sourceMappingURL=spark-liveness.js.map